import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { AppointmentDto, ContinuousAppointmentDto } from "../api-client";
import Controls from "../atoms/Controls";
import notificationStore from "../stores/Notification/notificationStore";
import { formatTime } from "../helpers/dateFormat";
import { TherapyChip, VisitTypeBadge } from "../atoms/Chip";
import { AllInclusive } from "@mui/icons-material";
import { mobiliTheme } from "../themes/mobiliTheme";
import { MobiliAvatar } from "../atoms/MobiliAvatar";
import { useCopyNewAppointment } from "../stores/Appointment/appointment.mutation";
import { slotTherapy, therapyColorKey } from "../helpers/slotTherapy";

interface Props {
  patientId: number;
  appointment: AppointmentDto | ContinuousAppointmentDto;
  isContinuous: boolean;
  isOpen: boolean;
  onClose: (actionPerformed: boolean) => void;
}

export const CopyAppointmentDialog: React.FC<Props> = ({
  patientId,
  appointment,
  isContinuous,
  isOpen,
  onClose,
}) => {
  const [selectedTimeslotIds, setSelectedTimeslotIds] = useState<string[]>([]);
  const [savingChanges, setSavingChanges] = useState(false);
  const { mutateAsync: copyAppointment } = useCopyNewAppointment();

  const timeSlots = useMemo(
    () => appointment.timeSlots ?? [],
    [appointment.timeSlots],
  );

  useEffect(() => {
    if (isOpen) {
      setSelectedTimeslotIds(
        timeSlots.map((slot: any) => slot.id).filter(Boolean),
      );
    }
  }, [isOpen, timeSlots]);

  const handleTimeslotToggle = (id: string) => {
    setSelectedTimeslotIds((prev) =>
      prev.includes(id)
        ? prev.filter((slotId) => slotId !== id)
        : [...prev, id],
    );
  };

  const allSelected =
    timeSlots.length > 0 && selectedTimeslotIds.length === timeSlots.length;

  const handleToggleAll = () => {
    if (allSelected) {
      setSelectedTimeslotIds([]);
    } else {
      setSelectedTimeslotIds(timeSlots.map((slot: any) => slot.id).filter(Boolean));
    }
  };

  const handleConfirm = async () => {
    if (!appointment.id) {
      notificationStore.showMessage(
        "Kein Termin gefunden, der kopiert werden kann.",
        "error",
      );
      return;
    }
    if (selectedTimeslotIds.length === 0) {
      notificationStore.showMessage(
        "Bitte wählen Sie mindestens einen Zeitslot zum Kopieren aus.",
        "warning",
      );
      return;
    }

    setSavingChanges(true);
    try {
      await copyAppointment({
        appointmentId: appointment.id,
        isContinuous,
        patientId,
        timeSlotIds: selectedTimeslotIds,
      });
      onClose(true);
    } catch (error: any) {
      console.error("Error copying appointment:", error);
    } finally {
      setSavingChanges(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Neuer Termin – Zeitslots auswählen</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 1,
            mb: 1,
          }}
        >
          <Typography variant="subtitle1">
            Zeitslots für die Kopie auswählen
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={allSelected}
                indeterminate={
                  selectedTimeslotIds.length > 0 && !allSelected
                }
                onChange={handleToggleAll}
              />
            }
            label="Alle"
          />
        </Box>
        <Box
          sx={{
            position: "relative",
            border: `1px solid ${mobiliTheme.mainHeader}`,
            p: 0,
            m: 0,
            paddingBottom: "4px",
            backgroundColor: mobiliTheme.lowLevelContainerBackground,
            borderRadius: "0.4em",
          }}
        >
          {timeSlots.map((timeSlot: any) => (
            <Grid container direction="column" key={timeSlot.id}>
              <Grid
                item
                xs={6}
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "flex-start",
                }}
              >
                {timeSlot && (
                  <Typography
                    noWrap
                    sx={{
                      fontSize: "0.75em",
                      fontWeight: "bold",
                      textDecoration: "underline",
                    }}
                  >
                    {!timeSlot.start && "nicht geplant"}
                    {timeSlot.start ? formatTime(timeSlot?.start as any) : " "}
                    {"-"}
                    {timeSlot.end ? formatTime(timeSlot.end as any) : ""}
                  </Typography>
                )}
              </Grid>

              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
              >
                <Grid item xs={3}>
                  {timeSlot.therapyRx?.rx?.rxNumber && (
                    <Tooltip title={timeSlot.therapyRx.rx.rxNumber}>
                      <Controls.Chip
                        label={timeSlot.therapyRx.rx.rxNumber}
                        sx={{
                          backgroundColor: "grey",
                          fontSize: "0.6em",
                          direction: "rtl",
                        }}
                        variant="outlined"
                      />
                    </Tooltip>
                  )}
                </Grid>
                <Grid item xs={2}>
                  {slotTherapy(timeSlot)?.abbreviation && (
                    <TherapyChip
                      therapyName={
                        (slotTherapy(timeSlot) as any).abbreviation
                      }
                      colorKey={therapyColorKey(timeSlot)}
                      sx={{ fontSize: "0.5em" }}
                    />
                  )}
                </Grid>
                <Grid item xs={1}>
                  {timeSlot?.user?.id && (
                    <MobiliAvatar
                      user={timeSlot?.user}
                      isTherapist
                      sx={{ width: 25, height: 25, fontSize: "0.8em" }}
                    />
                  )}
                </Grid>
                <Grid item xs={2}>
                  <VisitTypeBadge
                    visitType={appointment?.address?.type!}
                    visitFee={timeSlot?.visitFee?.abbreviation}
                  />
                </Grid>
                <Grid item xs={1}>
                  {timeSlot.therapyRx?.rx?.isPerpetual && (
                    <Tooltip title="">
                      <AllInclusive fontSize={"small"} />
                    </Tooltip>
                  )}
                </Grid>
                <Grid item xs={2}>
                  <Checkbox
                    checked={selectedTimeslotIds.includes(timeSlot.id)}
                    onChange={() => handleTimeslotToggle(timeSlot.id)}
                  />
                </Grid>
              </Grid>
            </Grid>
          ))}

          {timeSlots.length === 0 && (
            <Typography sx={{ fontSize: "12px", color: "grey.600", p: 1 }}>
              Keine Zeitslots vorhanden.
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        {savingChanges ? (
          <CircularProgress size={24} />
        ) : (
          <>
            <Button onClick={() => onClose(false)} variant="text">
              Abbrechen
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={selectedTimeslotIds.length === 0}
              variant="contained"
            >
              Kopieren
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CopyAppointmentDialog;
